import type { ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { SensitiveMoney } from "@/components/common/Sensitive";
import {
  ddEquipmentScopeLabel,
  ddPolicyTypeLabel,
  filterApplicableOverweightPolicies,
  filterApplicableSpotDnd,
  filterApplicableWeightLimitPolicies,
  formatFreeTime,
  formatKgAsTons,
  formatWeightLimit,
  hasBookingPolicies,
  hasLineWeight,
  policyEquipmentLabel,
  policyRouteLabel,
} from "@/lib/ratePolicies";
import type {
  BookingPolicies,
  DdPolicy,
  DndPolicy,
  OverweightPolicy,
  RateLineDdPolicy,
  WeightLimitPolicy,
} from "@/types/api";
import { CarrierContractLabel } from "@/components/common/CarrierContractLabel";
import type { RateLineRow } from "@/types/rate";
import { OverweightStepLabel } from "@/features/rates/OverweightStepLabel";

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-2">
      <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h4>
      {children}
    </section>
  );
}

function EmptyNote({ children }: { children: ReactNode }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}

function MetaRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium tabular-nums">{children}</span>
    </div>
  );
}

function PolicyCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children?: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border/60 bg-foreground/[0.03] p-3">
      <p className="text-sm font-medium leading-tight">{title}</p>
      {subtitle && (
        <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
      )}
      {children && <div className="mt-2 space-y-1">{children}</div>}
    </div>
  );
}

function OverweightList({ policies }: { policies: OverweightPolicy[] }) {
  if (policies.length === 0) return null;
  return (
    <div className="space-y-2">
      {policies.map((policy, index) => (
        <PolicyCard
          key={`${policy.origin_label ?? "all"}-${policy.destination_label ?? "all"}-${index}`}
          title={policyRouteLabel(policy.origin_label, policy.destination_label)}
          subtitle={`${policyEquipmentLabel(policy.equipment_sizes, policy.equipment_types)}${
            policy.weight_basis ? ` · ${policy.weight_basis}` : ""
          }`}
        >
          {policy.steps.map((step, stepIndex) => (
            <p key={stepIndex} className="text-xs">
              <OverweightStepLabel
                step={step}
                currency={policy.currency}
              />
            </p>
          ))}
        </PolicyCard>
      ))}
    </div>
  );
}

function WeightLimitList({ policies }: { policies: WeightLimitPolicy[] }) {
  if (policies.length === 0) return null;
  return (
    <div className="space-y-2">
      {policies.map((policy, index) => (
        <PolicyCard
          key={`${policy.limit_kg}-${policy.limit_unit}-${index}`}
          title={formatWeightLimit(policy)}
          subtitle={`${policyRouteLabel(policy.origin_label, policy.destination_label)} · ${policyEquipmentLabel(policy.equipment_sizes, policy.equipment_types)}`}
        />
      ))}
    </div>
  );
}

function BookingList({ policies }: { policies: BookingPolicies }) {
  return (
    <div className="space-y-1.5">
      {policies.max_containers != null && (
        <MetaRow label="Max containers">{policies.max_containers}</MetaRow>
      )}
      {policies.min_containers != null && (
        <MetaRow label="Min containers">{policies.min_containers}</MetaRow>
      )}
      {policies.min_teu != null && (
        <MetaRow label="Min TEU">{policies.min_teu} TEU</MetaRow>
      )}
    </div>
  );
}

function LineWeightSummary({ line }: { line: RateLineRow }) {
  const basis = line.max_weight_basis;
  const unit =
    line.weight_limit_unit === "per_teu"
      ? " / TEU"
      : line.weight_limit_unit === "per_container"
        ? " / container"
        : "";
  return (
    <div className="space-y-1.5">
      {line.max_weight != null && (
        <MetaRow label={basis ? `Max ${basis}` : "Max weight"}>
          {formatKgAsTons(line.max_weight)}t{unit}
        </MetaRow>
      )}
      {line.max_gross_kg != null && (
        <MetaRow label="Max gross">{formatKgAsTons(line.max_gross_kg)}t</MetaRow>
      )}
      {line.max_cargo_kg != null && (
        <MetaRow label="Max cargo">{formatKgAsTons(line.max_cargo_kg)}t</MetaRow>
      )}
    </div>
  );
}

function LineDdList({
  policies,
  freeTime,
}: {
  policies: RateLineDdPolicy[];
  freeTime: string | null;
}) {
  if (policies.length === 0 && !freeTime) return null;
  return (
    <div className="space-y-2">
      {freeTime && policies.length === 0 && (
        <MetaRow label="Free time">{freeTime}</MetaRow>
      )}
      {policies.map((policy) => (
        <PolicyCard
          key={policy.id}
          title={ddPolicyTypeLabel(policy.policy_type)}
          subtitle={`${policy.free_days} free ${policy.free_days === 1 ? "day" : "days"} · ${policy.currency}`}
        />
      ))}
    </div>
  );
}

function CardDdList({ policies }: { policies: DdPolicy[] }) {
  return (
    <div className="space-y-2">
      {policies.map((policy) => {
        const lastTier = policy.rate_tiers[policy.rate_tiers.length - 1];
        return (
          <PolicyCard
            key={policy.id}
            title={ddPolicyTypeLabel(policy.policy_type)}
            subtitle={`${policyRouteLabel(policy.origin_port, policy.destination_port)} · ${ddEquipmentScopeLabel(policy.equipment_size, policy.equipment_type)}`}
          >
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                {policy.free_days} free {policy.free_days === 1 ? "day" : "days"}
              </Badge>
              {policy.application_display && (
                <Badge variant="info" className="px-1.5 py-0 text-[10px]">
                  {policy.application_display}
                </Badge>
              )}
            </div>
            {lastTier && (
              <p className="text-xs text-muted-foreground">
                Top rate{" "}
                <SensitiveMoney amount={lastTier.rate_per_day} /> {lastTier.currency}
                /day
              </p>
            )}
          </PolicyCard>
        );
      })}
    </div>
  );
}

function SpotDndList({ policies }: { policies: DndPolicy[] }) {
  return (
    <div className="space-y-2">
      {policies.map((policy, index) => {
        const lastTier = policy.tiers[policy.tiers.length - 1];
        return (
          <PolicyCard
            key={`${policy.charge_type}-${policy.port_locode ?? "all"}-${index}`}
            title={policy.charge_type}
            subtitle={[
              policy.container_size_type,
              policy.port_locode,
              `${policy.freetime_grant_days} free days from ${policy.freetime_start_event}`,
            ]
              .filter(Boolean)
              .join(" · ")}
          >
            {lastTier && (
              <p className="text-xs text-muted-foreground">
                Top rate{" "}
                <SensitiveMoney amount={lastTier.charge_per_diem} />{" "}
                {lastTier.currency}/day
              </p>
            )}
          </PolicyCard>
        );
      })}
    </div>
  );
}

interface RateCardPolicySectionsProps {
  carrier?: string | null;
  contractRef?: string | null;
  overweightPolicies: OverweightPolicy[];
  weightLimitPolicies: WeightLimitPolicy[];
  bookingPolicies: BookingPolicies | null;
  ddPolicies: DdPolicy[];
  spotDndPolicies: DndPolicy[];
  ddLoading?: boolean;
}

function CardIdentity({
  carrier,
  contractRef,
}: {
  carrier?: string | null;
  contractRef?: string | null;
}) {
  if (!carrier && !contractRef) return null;
  return (
    <div className="rounded-lg border border-border/60 bg-foreground/[0.03] p-3">
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Rate card
      </p>
      <CarrierContractLabel
        carrier={carrier}
        contractRef={contractRef}
        size={18}
        bold
        contractClassName="text-sm font-semibold"
      />
    </div>
  );
}

/** Global weight / booking / D&D policies for a rate card (no line selected). */
export function RateCardPolicySections({
  carrier,
  contractRef,
  overweightPolicies,
  weightLimitPolicies,
  bookingPolicies,
  ddPolicies,
  spotDndPolicies,
  ddLoading = false,
}: RateCardPolicySectionsProps) {
  const hasWeight =
    overweightPolicies.length > 0 || weightLimitPolicies.length > 0;
  const hasBooking = hasBookingPolicies(bookingPolicies);
  const hasDd = ddPolicies.length > 0 || spotDndPolicies.length > 0;
  const identity = (
    <CardIdentity carrier={carrier} contractRef={contractRef} />
  );

  if (!hasWeight && !hasBooking && !hasDd && !ddLoading) {
    return (
      <div className="space-y-4">
        {identity}
        <EmptyNote>
          No weight, booking, or D&D policies on this rate card.
        </EmptyNote>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {identity}
      {hasWeight && (
        <Section title="Weight">
          <div className="space-y-2">
            <WeightLimitList policies={weightLimitPolicies} />
            <OverweightList policies={overweightPolicies} />
          </div>
        </Section>
      )}
      {hasBooking && bookingPolicies && (
        <Section title="Booking">
          <BookingList policies={bookingPolicies} />
        </Section>
      )}
      {(hasDd || ddLoading) && (
        <Section title="D&D">
          {ddPolicies.length > 0 ? (
            <CardDdList policies={ddPolicies} />
          ) : spotDndPolicies.length > 0 ? (
            <SpotDndList policies={spotDndPolicies} />
          ) : (
            <EmptyNote>Loading D&D policies…</EmptyNote>
          )}
        </Section>
      )}
    </div>
  );
}

/** Line-scoped policies shown inside the surcharge breakdown panel. */
export function RateLinePolicySections({ line }: { line: RateLineRow }) {
  const card = line.cardPolicies;
  const overweight = card
    ? filterApplicableOverweightPolicies(
        card.overweight_policies,
        line.equipment_size,
        line.equipment_type,
        line.origin_port.locode,
      )
    : [];
  const weightLimits =
    !hasLineWeight(line) && card
      ? filterApplicableWeightLimitPolicies(
          card.weight_limit_policies,
          line.equipment_size,
          line.equipment_type,
          line.origin_port.locode,
        )
      : [];
  const booking = card?.booking_policies ?? null;
  const freeTime = formatFreeTime(line.free_time_days, line.free_time_days_max);
  const spotDnd =
    line.dd_policies.length === 0 && card
      ? filterApplicableSpotDnd(
          card.dnd_policies,
          line.equipment_size,
          line.equipment_type,
          line.destination_port.locode,
        )
      : [];

  const hasWeight =
    hasLineWeight(line) || overweight.length > 0 || weightLimits.length > 0;
  const hasBooking = hasBookingPolicies(booking);
  const hasDd =
    line.dd_policies.length > 0 || freeTime != null || spotDnd.length > 0;

  const identity = (
    <CardIdentity carrier={line.carrier} contractRef={line.contract_ref} />
  );

  if (!hasWeight && !hasBooking && !hasDd) {
    return (
      <div className="space-y-4">
        {identity}
        <EmptyNote>No policies apply to this rate line.</EmptyNote>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {identity}
      {hasWeight && (
        <Section title="Weight">
          <div className="space-y-2">
            {hasLineWeight(line) && <LineWeightSummary line={line} />}
            <WeightLimitList policies={weightLimits} />
            <OverweightList policies={overweight} />
          </div>
        </Section>
      )}
      {hasBooking && booking && (
        <Section title="Booking">
          <BookingList policies={booking} />
        </Section>
      )}
      {hasDd && (
        <Section title="D&D">
          <LineDdList policies={line.dd_policies} freeTime={freeTime} />
          {spotDnd.length > 0 && <SpotDndList policies={spotDnd} />}
        </Section>
      )}
    </div>
  );
}
